import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput, { formatINR } from '../../components/SliderInput';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const FDCalculator = () => {
  const [principal, setPrincipal] = useState(100000);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(5);
  const [freq, setFreq] = useState('quarterly');

  const { maturity, interest } = useMemo(() => {
    const r = rate / 100;
    const n = freq === 'monthly' ? 12 : freq === 'quarterly' ? 4 : freq === 'half' ? 2 : 1;
    const m = principal * Math.pow(1 + r / n, n * years);
    return { maturity: m, interest: m - principal };
  }, [principal, rate, years, freq]);

  return (
    <CalculatorLayout
      title="FD Calculator"
      subtitle="Calculate maturity value of your Fixed Deposit with compound interest."
      category="Financial"
      description={<>
        <p>A Fixed Deposit (FD) is one of India's most popular safe investment options. Banks pay a fixed interest rate compounded periodically (typically quarterly).</p>
        <p>Use this calculator to find the maturity amount of your FD based on principal, interest rate, tenure, and compounding frequency.</p>
      </>}
      formulaNote="A = P × (1 + r/n)^(n×t)    where P = principal, r = annual rate, n = compounding/year, t = years"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <SliderInput label="Principal Amount" unit="₹" unitColor="saffron" value={principal} onChange={setPrincipal} min={1000} max={10000000} step={1000} format={formatINR} />
          <SliderInput label="Interest Rate" unit="%" unitColor="green" value={rate} onChange={setRate} min={1} max={15} step={0.1} />
          <SliderInput label="Tenure" unit="yr" value={years} onChange={setYears} min={0.25} max={20} step={0.25} />
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Compounding Frequency</label>
            <Tabs value={freq} onValueChange={setFreq}>
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="quarterly">Quarterly</TabsTrigger>
                <TabsTrigger value="half">Half-Yr</TabsTrigger>
                <TabsTrigger value="yearly">Yearly</TabsTrigger>
              </TabsList>
              <TabsContent value="monthly" />
              <TabsContent value="quarterly" />
              <TabsContent value="half" />
              <TabsContent value="yearly" />
            </Tabs>
          </div>
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Maturity Amount</div>
          <div className="font-display text-3xl md:text-4xl font-bold text-bharat-navy display-font mt-1">{formatINR(maturity)}</div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <Card label="Principal" value={formatINR(principal)} />
            <Card label="Interest Earned" value={formatINR(interest)} tone="green" />
          </div>
          <div className="mt-5 p-3 bg-white/70 border border-saffron-100 rounded-lg text-xs text-slate-600">
            💡 Tip: Senior citizens typically get 0.5–0.75% extra interest on FDs in India.
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Card = ({ label, value, tone }) => {
  const c = tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-3 border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font font-semibold mt-0.5 ${c}`}>{value}</div>
    </div>
  );
};

export default FDCalculator;
